import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Button, Group, SegmentedControl, Text, Title } from "@mantine/core"
import type { TWorldEntityFilter } from "../useWorldEntityGrid"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

/**
 * The shared toolbar row for the world-entity CRUD grids: a heading + live
 * accepted/pending counts on the left, and the filter segment, conditional
 * bulk buttons, and Add button on the right. `leadingControls` slots extra
 * controls (Locations' grid/tree toggle) before the filter segment.
 *
 * Bulk buttons appear only on the Pending filter when pending entries exist —
 * the same rule every view carried inline. Where the confirm dialog lives
 * (view vs. wrapper) and how bulk-busy is tracked stay behind the callbacks,
 * so this component's contract is uniform across all three entities.
 */
export const WorldEntityToolbar = ({
  title,
  acceptedCount,
  pendingCount,
  filter,
  onFilterChange,
  bulkBusy,
  onAcceptAllPending,
  onDeleteAllPending,
  addLabel,
  onAdd,
  leadingControls,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const showBulkActions = filter === "pending" && pendingCount > 0

  return (
    <div className={classes.toolbar}>
      <Group gap="md">
        <Title order={4}>{title}</Title>
        <Text fz="xs" c="dimmed">
          <Trans>
            {acceptedCount} accepted · {pendingCount} pending
          </Trans>
        </Text>
      </Group>
      <Group gap="sm">
        {leadingControls}
        <SegmentedControl
          size="xs"
          value={filter}
          onChange={(value) => onFilterChange(value as TWorldEntityFilter)}
          data={[
            { value: "all", label: i18n._(msg`All`) },
            { value: "pending", label: i18n._(msg`Pending`) },
            { value: "accepted", label: i18n._(msg`Accepted`) },
          ]}
        />
        {showBulkActions && (
          <>
            <Button size="xs" color="success" variant="light" loading={bulkBusy} onClick={onAcceptAllPending}>
              <Trans>Accept all</Trans>
            </Button>
            <Button size="xs" color="danger" variant="light" loading={bulkBusy} onClick={onDeleteAllPending}>
              <Trans>Delete all</Trans>
            </Button>
          </>
        )}
        <Button size="xs" onClick={onAdd}>
          {addLabel}
        </Button>
      </Group>
    </div>
  )
}
